import { Icon } from '@/components/Icon/Icon';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

/**
 * Determines if the current date falls within the Christmas period
 * @returns {boolean} True if the current date is between December 1-26
 */
const isChristmasPeriod = (): boolean => {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed, so December is 11
  const day = now.getDate();

  return month === 11 && day >= 1 && day <= 26;
};

/**
 * Gets the appropriate default avatar based on the current date
 * @returns {string} Path to the default avatar image
 */
const getDefaultAvatar = (): string =>
  isChristmasPeriod()
    ? '/img/team/defaultAvatarChristmas.png'
    : '/img/team/defaultAvatar.jpg';

export const Avatar = ({
  url,
  className,
  onDeleteClicked,
  disabled,
}: {
  className?: string;
  url: string | undefined | null;
  disabled?: boolean;
  onDeleteClicked?: () => void;
}) => {
  const t = useTranslations();

  const canDeleteAvatar = onDeleteClicked && !disabled && url;

  return (
    <div
      className={cn(
        'group relative flex shrink-0 grow-0 items-center justify-center overflow-hidden rounded-max border border-colors-borders-secondary bg-colors-content-interactive-accent',
        className,
      )}
    >
      {canDeleteAvatar && (
        <>
          <div className="invisible absolute inset-0 z-10 flex items-center justify-center group-hover:visible">
            <div className="absolute inset-0 bg-colors-content-primary opacity-50"></div>
            <Icon
              icon="XClose"
              className="relative z-20 h-600 w-600 text-colors-backgrounds-brand-accent"
            />
          </div>
          <button
            type="button"
            className="absolute inset-0 z-30"
            onClick={(event) => {
              event.preventDefault();
              onDeleteClicked();
            }}
          />
        </>
      )}
      <Image
        src={url ?? getDefaultAvatar()}
        alt={t('avatar_alt')}
        fill
        className={cn('object-cover', disabled && 'opacity-50')}
      />
    </div>
  );
};
